import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { HeaderFive, TabButton, TabSet } from 'secondstep-components'
import { useLocation } from 'react-router-dom'
import Badge from 'components/Badge'
import Licenses from 'components/Licenses'
import LicensesUnassigned from 'components/LicensesUnassigned'
import LicensesInactive from 'components/LicensesInactive'

import {
  LICENSE_LISTS_ASSIGNED_TEXT,
  LICENSE_LISTS_ASSIGNED_TAB_TEXT,
  LICENSE_LISTS_EXPIRED_TEXT,
  LICENSE_LISTS_EXPIRED_TAB_TEXT,
  LICENSE_LISTS_UNASSIGNED_TEXT,
  LICENSE_LISTS_UNASSIGNED_TAB_TEXT,
} from './constants'
import { paths } from 'components/LUMRouter/paths'

import {
  LicenseListsUnassignedText,
  LicenseListsExpiredText,
} from './component.styles'

const TABS = {
  Unassigned: 1,
  Assigned: 2,
  Inactive: 3,
}

const TAB_CONFIG = {
  [TABS.Unassigned]: {
    url: paths.UNASSIGNED_LICENSES,
    tabText: LICENSE_LISTS_UNASSIGNED_TAB_TEXT,
    emptyText: LICENSE_LISTS_UNASSIGNED_TEXT,
    testId: 'tabbutton-licenses-unassigned',
    badgeTestId: 'badge-licenses-unassigned',
  },
  [TABS.Assigned]: {
    url: paths.ASSIGNED_LICENSES,
    tabText: LICENSE_LISTS_ASSIGNED_TAB_TEXT,
    emptyText: LICENSE_LISTS_ASSIGNED_TEXT,
    testId: 'tabbutton-licenses-assigned',
    badgeTestId: 'badge-licenses',
  },
  [TABS.Inactive]: {
    url: paths.INACTIVE_LICENSES,
    tabText: LICENSE_LISTS_EXPIRED_TAB_TEXT,
    emptyText: LICENSE_LISTS_EXPIRED_TEXT,
    testId: 'tabbutton-licenses-inactive',
    badgeTestId: 'badge-licenses-expired',
  },
}

const URL_TO_TAB = {
  [paths.BASE_LICENSE_MANAGEMENT]: TABS.Unassigned,
  [paths.UNASSIGNED_LICENSES]: TABS.Unassigned,
  [paths.ASSIGNED_LICENSES]: TABS.Assigned,
  [paths.INACTIVE_LICENSES]: TABS.Inactive,
}

const TabLabel = ({ text, count, badgeTestId }) => (
  <HeaderFive>
    {text}
    <Badge color="default" data-testid={badgeTestId}>
      {count}
    </Badge>
  </HeaderFive>
)

TabLabel.propTypes = {
  badgeTestId: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
}

const LicenseLists = ({
  licenses,
  licensesInactive,
  licensesUnassigned,
  onSelectAssign,
  onSelectLicense,
  onSelectUserManagement,
  pushTo,
}) => {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(TABS.Unassigned)

  const setCurrentTab = useCallback(
    tab => {
      if (activeTab !== tab) {
        pushTo(TAB_CONFIG[tab].url)
        setActiveTab(tab)
      }
    },
    [activeTab, pushTo],
  )

  useEffect(() => {
    const pathName = location.pathname
    const tabFromUrl = URL_TO_TAB[pathName]

    // If there are no unassigned licenses, default to Assigned tab
    if (licensesUnassigned?.length === 0 && tabFromUrl === TABS.Unassigned) {
      setCurrentTab(TABS.Assigned)
    } else if (tabFromUrl) {
      setCurrentTab(tabFromUrl)
    }
  }, [licensesUnassigned?.length, location.pathname, setCurrentTab])

  const tabData = useMemo(
    () => ({
      [TABS.Unassigned]: {
        licenses: licensesUnassigned,
        emptyComponent: LicenseListsUnassignedText,
        listComponent: LicensesUnassigned,
        listProps: {
          licenses: licensesUnassigned,
          onSelectAssign,
          onSelectLicense,
        },
      },
      [TABS.Assigned]: {
        licenses,
        emptyComponent: LicenseListsUnassignedText,
        listComponent: Licenses,
        listProps: {
          licenses,
          onSelectUserManagement,
        },
      },
      [TABS.Inactive]: {
        licenses: licensesInactive,
        emptyComponent: LicenseListsExpiredText,
        listComponent: LicensesInactive,
        listProps: {
          licenses: licensesInactive,
        },
      },
    }),
    [
      licenses,
      licensesInactive,
      licensesUnassigned,
      onSelectAssign,
      onSelectLicense,
      onSelectUserManagement,
    ],
  )

  const renderTabContent = useCallback(
    tab => {
      const {
        licenses: tabLicenses,
        emptyComponent: EmptyComponent,
        listComponent: ListComponent,
        listProps,
      } = tabData[tab]
      const config = TAB_CONFIG[tab]

      if (tabLicenses?.length === 0) {
        return (
          <EmptyComponent>
            <p>{config.emptyText}</p>
          </EmptyComponent>
        )
      }

      return <ListComponent {...listProps} />
    },
    [tabData],
  )

  const tabs = useMemo(
    () => [
      {
        tab: TABS.Unassigned,
        config: TAB_CONFIG[TABS.Unassigned],
        count: licensesUnassigned?.length || 0,
        alwaysVisible: true,
      },
      {
        tab: TABS.Assigned,
        config: TAB_CONFIG[TABS.Assigned],
        count: licenses?.length || 0,
        alwaysVisible: true,
      },
      {
        tab: TABS.Inactive,
        config: TAB_CONFIG[TABS.Inactive],
        count: licensesInactive?.length || 0,
        alwaysVisible: false,
      },
    ],
    [licenses?.length, licensesInactive?.length, licensesUnassigned?.length],
  )

  return (
    <>
      <TabSet dataTestid="tabset-licenses">
        {tabs
          .filter(({ alwaysVisible, count }) => alwaysVisible || count > 0)
          .map(({ tab, config, count }) => (
            <TabButton
              dataTestId={config.testId}
              isActive={activeTab === tab}
              key={tab}
              label={
                <TabLabel
                  badgeTestId={config.badgeTestId}
                  count={count}
                  text={config.tabText}
                />
              }
              onClick={() => setCurrentTab(tab)}
            />
          ))}
      </TabSet>

      {renderTabContent(activeTab)}
    </>
  )
}

LicenseLists.propTypes = {
  licenses: PropTypes.arrayOf(PropTypes.object).isRequired,
  licensesInactive: PropTypes.arrayOf(PropTypes.object).isRequired,
  licensesUnassigned: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectAssign: PropTypes.func.isRequired,
  onSelectLicense: PropTypes.func.isRequired,
  onSelectUserManagement: PropTypes.func.isRequired,
  pushTo: PropTypes.func.isRequired,
}

export default LicenseLists
